import Model, { attr } from '@ember-data/model';

import type { DSSetup } from 'webapp-types';
import { CompareFunction, MetricStatus, MetricType, MetricValueType } from 'webapp/enums/enums';
import type { VerificationInfo } from 'webapp/utils/verification';

export default class MetricRecord extends Model {
	@attr('string') name!: string;
	@attr('string') description!: string;
	@attr('string') key!: string; // this is the string for metricSetup.key
	@attr('string') objectType!: MetricType;
	@attr('number') objectId!: number; // script / cq / goal id, only present for METRIC_CALCULATION / CUSTOM_QUERY / GOAL objectTypes. the _GENERIC types only have these when isPublic is on (predefined generics), and references id from the predefined datablocks space
	@attr('string') dataSourceTypeKey!: string; // sourceType.key
	@attr('number') spaceAccessId?: number; // not present for BASIC, CUSTOM_QUERY_GENERIC & METRIC_CALCULATION_GENERIC objectTypes
	@attr('string') metricId?: string; // for CUSTOM_PUSH objectType only
	@attr('number', { defaultValue: 0 }) usedCount!: number; // only for generics
	@attr('boolean', { defaultValue: false }) isPublic!: boolean; // true for public predefined generics
	@attr('string', { defaultValue: MetricStatus.ACTIVE }) status!: MetricStatus; // hide disabled metrics in metric-select
	@attr('array', {
		defaultValue() {
			return [];
		},
	})
	dimensions!: DSSetup.DimensionItem[]; // if empty, considered as non-attributed / non-segmentable
	@attr('boolean') hasUnits!: boolean;
	@attr('boolean') isStandalone!: boolean; // if false, always must choose a dimension
	@attr('boolean', { defaultValue: false }) noReset!: boolean;
	@attr('string') format?: string; // fallback to "Auto"
	@attr('string') changeFormat?: string; // fallback to "+0%""
	@attr('boolean', { defaultValue: false }) changeInvert!: boolean;
	@attr('number', { defaultValue: CompareFunction.PERCENTAGE }) changeFunction!: CompareFunction; // CompareFunction (1 perc/2 ratio/3 absolute) -defaults to 1 perc

	// normalized from settings (for CUSTOM_PUSH objectType only)
	@attr('string', { defaultValue: MetricValueType.EVENT }) metricType!: MetricValueType; // Type set to MetricValueType but it's not always set (can be null)
	@attr('string', { defaultValue: 'SUM' }) aggregationFunction!: string | null;
	@attr('number') granulationOrigin!: number | null;
	@attr('string') dimension!: string | null;
	@attr('boolean', { defaultValue: true }) hasFutureData!: boolean;
	@attr('boolean', { defaultValue: true }) isHistoryChanging!: boolean;
	@attr('boolean', { defaultValue: false }) ensureUnique!: boolean;
	@attr('boolean', { defaultValue: false }) isAppend!: boolean; // used in custom push integrations to allow pushing more data to same timestamp
	@attr('boolean', { defaultValue: false }) hasMedia!: boolean;
	@attr('boolean', { defaultValue: null }) isHistoricalDataPersistenceEnabled!: boolean | null; // used in "historicalDataPersistenceSupported" integrations, related to how dataservice prepares data. more info: https://databox.slack.com/archives/C8JCR5AKH/p1718190519254689?thread_ts=1718012476.356259&cid=C8JCR5AKH / https://app.asana.com/0/0/1206875629609996/1207600942211788/f
	@attr('object') verificationInfo?: VerificationInfo;

	// helpers
	@attr('string') cloneFrom!: string | null; // not sent to BE, used when duplicating a metric

	get isTypeBasic(): boolean {
		return this.objectType === MetricType.BASIC;
	}

	get isTypeMetricCalculation(): boolean {
		return this.objectType === MetricType.METRIC_CALCULATION;
	}

	get isTypeCustomQuery(): boolean {
		return this.objectType === MetricType.CUSTOM_QUERY;
	}

	get isTypeCustomPush(): boolean {
		return this.objectType === MetricType.CUSTOM_PUSH;
	}

	get isTypeGoal(): boolean {
		return this.objectType === MetricType.GOAL;
	}

	get isTypeCustomQueryGeneric(): boolean {
		return this.objectType === MetricType.CUSTOM_QUERY_GENERIC;
	}

	get isTypeMetricCalculationGeneric(): boolean {
		return this.objectType === MetricType.METRIC_CALCULATION_GENERIC;
	}

	get isTypeHorizon(): boolean {
		return this.objectType === MetricType.HORIZON;
	}

	get isTypeCustomQueryOrGeneric(): boolean {
		// any custom query, incl. generic
		return this.isTypeCustomQuery || this.isTypeCustomQueryGeneric;
	}

	get isTypeBuilt(): boolean {
		return this.isTypeCustomQueryOrGeneric || this.isTypeHorizon;
	}

	get isTypeMetricCalculationOrGeneric(): boolean {
		// any script, incl. generic
		return this.isTypeMetricCalculation || this.isTypeMetricCalculationGeneric;
	}

	get isOnSource() {
		return !!this.spaceAccessId;
	}

	get isGenericBasic() {
		// cloud source basic metrics (only on generic source)
		return this.isTypeBasic && !this.isOnSource;
	}

	get isGenericCustom() {
		// generic script / cq (also from predefined space)
		return this.isTypeCustomQueryGeneric || this.isTypeMetricCalculationGeneric;
	}

	get isGeneric() {
		// all generics on all sources
		return this.isGenericCustom || this.isGenericBasic;
	}

	get isPredefined() {
		// generic public predefined cq / scripts (and its copies on sources)
		return this.isGenericCustom && this.isPublic;
	}

	get isUserGeneric() {
		// user's generic cq / scripts (not predefined)
		return this.isGenericCustom && !this.isPublic;
	}

	get isPredefinedOnGenericSource() {
		// generic public redefined cq / scripts (only on generic source)
		return this.isPredefined && !this.isOnSource;
	}

	get metricData(): DSSetup.MetricData | null {
		const key = this.key;
		if (key) {
			return {
				key,
				spaceAccessId: this.spaceAccessId || null,
			};
		}
		return null;
	}
}
